import React, { useState, useMemo, useEffect, Fragment, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { PersonaFormData } from "../../../../../../../stores/types/personasTypes.tsx";
import {
  type APILocation,
  RevButton,
  RevCard,
  RevCheckbox,
  RevDialog,
  RevDragAndDrop,
  RevInput,
  RevLocation,
  RevRadioGroup,
  RevSelect,
  RevSlider,
  RevTextArea,
} from "@revmatics/catalyst";
import { instance } from "../../../../../../../stores/axios/axiosConfig.ts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-light-svg-icons";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import { RevInterestsInput } from "./interestsInput/RevInterestsInput.tsx";
import {
  genderRadioOptions,
  educationOptions,
  occupationOptions,
  maritalStatusOptions,
  familySizeOptions,
  browseMethodOptions,
  buyingMethodOptions,
  ageSelectOptions,
} from "./data/personaFormData.ts";
import {
  hasValidDomain,
  isValidUrl,
} from "../../../../../../../helpers/functions/hasValidHelpers.ts";
import { useUnsavedChangesDialog } from "../../../../../../../helpers/hooks/useUnsavedChangesDialog.tsx";
import { useAppSelector } from "../../../../../../../stores/hooks/hooks.ts";
import { useTrimmedRegister } from "../../../../../../../helpers/hooks/useTrimmedRegister.ts";

type Props = {
  setHref: (href: string) => void;
  setOpenUnsavedModal: (value: boolean) => void;
  setUnsavedChanges?: (value: boolean) => void;
  hasUnsavedChanges?: boolean;
};

export const PersonaForm = ({
  setHref,
  setOpenUnsavedModal,
  setUnsavedChanges,
  hasUnsavedChanges = false,
}: Props) => {
  const {
    control,
    formState: { errors, isDirty },
    setValue,
    getValues,
    watch,
    register,
  } = useFormContext<PersonaFormData>();
  const lastChangedRef = useRef<"main" | "secondary" | null>(null);
  const [showUrlDialog, setShowUrlDialog] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [imageUrlPreview, setImageUrlPreview] = useState<string | null>(null);
  const [debouncedUrl, setDebouncedUrl] = useState("");

  const { coreValues, selectedPersona } = useAppSelector(
    (state) => state.personas,
  );

  const userProfile = useAppSelector((state) => state.profile.profile);
  const canManagePlatform = useMemo(() => {
    return (
      userProfile?.roles?.includes("INTERNAL_PLATFORM_CONTENT_MANAGER") ?? false
    );
  }, [userProfile]);

  const personaPictureFromForm = watch("persona_picture");
  const personaPictureUrlFromForm = watch("persona_picture_url");
  const ageFromValue = watch("age_from");
  const ageToValue = watch("age_to");
  const mainValue = watch("main_core_value");
  const secondaryValue = watch("secondary_core_value");

  const ageToOptions = useMemo(() => {
    if (!ageFromValue) {
      return ageSelectOptions;
    }
    const fromAge = parseInt(String(ageFromValue), 10);
    return ageSelectOptions.filter(
      (option) => parseInt(String(option.value), 10) >= fromAge,
    );
  }, [ageFromValue]);

  const ageFromOptions = useMemo(() => {
    if (!ageToValue) {
      return ageSelectOptions;
    }
    const toAge = parseInt(String(ageToValue), 10);
    return ageSelectOptions.filter(
      (option) => parseInt(String(option.value), 10) <= toAge,
    );
  }, [ageToValue]);

  useEffect(() => {
    if (!mainValue || !secondaryValue) return;

    if (mainValue === secondaryValue) {
      if (lastChangedRef.current === "main") {
        setValue("secondary_core_value", "", { shouldDirty: true });
      } else if (lastChangedRef.current === "secondary") {
        setValue("main_core_value", "", { shouldDirty: true });
      }
    }
  }, [mainValue, secondaryValue, setValue]);

  useEffect(() => {
    if (setUnsavedChanges) {
      setUnsavedChanges(
        isDirty ||
          (selectedPersona
            ? personaPictureFromForm !== selectedPersona?.persona_picture
            : !!personaPictureFromForm),
      );
    }
  }, [isDirty, personaPictureFromForm, selectedPersona, setUnsavedChanges]);

  const registerTrimmed = useTrimmedRegister(register);

  const fetchCities = async (query: string): Promise<APILocation[]> => {
    if (!query) return [];
    try {
      const response = await instance.get(
        `/api/cities/typeahead/?limit=10&offset=0&q=${query}`,
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch cities:", error);
      return [];
    }
  };

  useUnsavedChangesDialog({
    hasUnsavedChanges,
    onNavigateAttempt: (href) => {
      setHref(href);
      setOpenUnsavedModal(true);
    },
  });

  const sanitizeImageUrl = (url: string): string | null => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return parsed.href;
      }
      return null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (!debouncedUrl || !hasValidDomain(debouncedUrl)) {
        setImageUrlPreview(null);
        return;
      }

      const normalizedUrl = isValidUrl(debouncedUrl)
        ? debouncedUrl
        : `https://${debouncedUrl}`;

      const safeUrl = sanitizeImageUrl(normalizedUrl);

      setImageUrlPreview(safeUrl);
    }, 500);

    return () => clearTimeout(handler);
  }, [debouncedUrl]);

  const handleUrlInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrlInput(e.target.value);
    setDebouncedUrl(e.target.value);
  };

  const handleAddUrlImage = () => {
    if (imageUrlPreview) {
      setValue("persona_picture_url", imageUrlPreview, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue("persona_picture", null, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setShowUrlDialog(false);
    }
  };

  const openFileSystemViaDropzoneRef = useRef<() => void>(null);

  const openFileSystem = () => {
    if (openFileSystemViaDropzoneRef.current) {
      openFileSystemViaDropzoneRef.current();
    } else {
      const dndInput = document.querySelector(
        'input[name="persona_picture_dnd_input"]',
      );
      if (dndInput instanceof HTMLElement) {
        dndInput.click();
      } else {
        console.log(
          "Cannot programmatically open file dialog for RevDragAndDrop.",
        );
      }
    }
  };

  const handleRemovePicture = () => {
    setValue("persona_picture", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue("persona_picture_url", null, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const coreValuesOptions = useMemo(() => {
    if (!coreValues) return [];
    return coreValues.map((item) => ({
      value: item.id.toString(),
      label: item.name,
    }));
  }, [coreValues]);

  const imageExists = !!(personaPictureFromForm || personaPictureUrlFromForm);

  return (
    <div className="space-y-6">
      <RevCard className="py-4 px-6" title="General" paddingTitle="py-4 px-6">
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start gap-x-6 gap-y-4">
            <div className="w-full sm:w-auto sm:max-w-[160px] flex-shrink-0">
              <Controller
                name="persona_picture"
                control={control}
                render={({
                  field: {
                    onChange: onRHFFileChange,
                    value: personaPictureFile,
                  },
                  fieldState,
                }) => {
                  const displayImageSource =
                    personaPictureFile || personaPictureUrlFromForm;

                  return (
                    <RevDragAndDrop
                      name="persona_picture_dnd_input"
                      onFileChange={(file) => {
                        onRHFFileChange(file);
                        if (file) {
                          setValue("persona_picture_url", null, {
                            shouldDirty: true,
                          });
                        }
                      }}
                      initialImageUrl={displayImageSource}
                      error={fieldState.error}
                      hideRemoveButton={true}
                      dropzoneClassName="!h-[160px] !max-h-[160px] !w-[160px]"
                    />
                  );
                }}
              />
            </div>

            <div>
              <p className="typography-body-bold-sm text-monochrome-900 mb-4">
                Persona picture
              </p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-2">
                <Menu as="div" className="relative inline-block text-left">
                  <MenuButton
                    as={RevButton}
                    text={imageExists ? "Change picture" : "Add picture"}
                    variant="secondary"
                    buttonSize="extraSmall"
                    dropdownBtn={true}
                  />
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute left-0 mt-2 w-56 origin-top-left rounded-md bg-white shadow-lg focus:outline-none z-10">
                      <div className="py-1">
                        <MenuItem>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={openFileSystem}
                              className={`text-monochrome-900 ${
                                active ? "bg-monochrome-50" : ""
                              } group flex w-full items-center px-2 py-2 text-sm`}
                            >
                              From your computer
                            </button>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => {
                                setImageUrlInput(
                                  getValues("persona_picture_url") || "",
                                );
                                setDebouncedUrl(
                                  getValues("persona_picture_url") || "",
                                );
                                setShowUrlDialog(true);
                              }}
                              className={`text-monochrome-900 ${
                                active ? "bg-monochrome-50" : ""
                              } group flex w-full items-center px-2 py-2 text-sm`}
                            >
                              Via a URL
                            </button>
                          )}
                        </MenuItem>
                        <MenuItem disabled>
                          <span className="opacity-50 group flex w-full items-center px-2 py-2 text-sm text-monochrome-400 cursor-normal">
                            Generate Image (coming soon)
                          </span>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
                {imageExists && (
                  <RevButton
                    text="Remove picture"
                    variant="secondary"
                    buttonColor="error"
                    buttonSize="extraSmall"
                    handleClick={handleRemovePicture}
                    className="w-fit"
                  />
                )}
              </div>
              <p className="typography-body-xs text-monochrome-500">
                Use SVG, PNG, JPG (max. 1MB). Best size: 200x200 pixels.
              </p>
            </div>
          </div>

          <RevInput
            name="name"
            label="Persona Name"
            placeholder="Enter name"
            type="text"
            size="md"
            isLabelBold
            required
            register={registerTrimmed("name", {
              required: "Persona name is required.",
              maxLength: {
                value: 30,
                message: "Maximum 30 characters allowed.",
              },
            })}
            maxLength={30}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            error={errors.name}
          />
          <RevTextArea
            name="needs"
            label="Needs"
            placeholder="Enter needs"
            size="md"
            isLabelBold
            rows={5}
            register={registerTrimmed("needs")}
            error={errors.needs}
            getValues={getValues}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <RevTextArea
              name="concerns"
              label="Concerns"
              placeholder="Enter concerns"
              size="md"
              isLabelBold
              rows={5}
              register={registerTrimmed("concerns")}
              error={errors.concerns}
              getValues={getValues}
            />
            <RevTextArea
              name="motivations"
              label="Motivations"
              placeholder="Enter motivations"
              size="md"
              isLabelBold
              rows={5}
              register={registerTrimmed("motivations")}
              error={errors.motivations}
              getValues={getValues}
            />
          </div>
          {canManagePlatform && (
            <Controller
              name="is_platform"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <div className="mt-4">
                  <RevCheckbox
                    label="Platform Persona"
                    checked={field.value || false}
                    handleChecked={() => field.onChange(!field.value)}
                  />
                  <p className="typography-body-xs text-monochrome-500 mt-1 ml-7">
                    Platform personas are shared across all customers and
                    managed by Revmatics staff.
                  </p>
                </div>
              )}
            />
          )}
        </div>
      </RevCard>
      <RevCard
        className="py-4 px-6"
        title="Demographics"
        paddingTitle="py-4 px-6"
      >
        <div className="space-y-6">
          <div>
            <p className="typography-body-bold-sm mb-3 text-monochrome-900">
              Age
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-[132px_132px] gap-4">
              <RevSelect
                name="age_from"
                label="From"
                options={ageFromOptions}
                control={control}
                error={errors.age_from}
                size="md"
                placeholder="Age"
              />
              <RevSelect
                name="age_to"
                label="To"
                options={ageToOptions}
                control={control}
                error={errors.age_to}
                size="md"
                placeholder="Age"
              />
            </div>
          </div>

          <RevRadioGroup
            name="gender"
            label="Gender"
            options={genderRadioOptions}
            control={control}
            displayDirection="horizontal"
            error={errors.gender}
            isLabelBold
          />
          <div>
            <p className="typography-body-bold-sm mb-3 text-monochrome-900">
              Income
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-[132px_132px] gap-4">
              <Controller
                name="income_from"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RevInput
                    {...field}
                    label="From"
                    type="text"
                    size="md"
                    prependText="$"
                    error={errors.income_from}
                    mask="currency"
                  />
                )}
              />
              <Controller
                name="income_to"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RevInput
                    {...field}
                    label="To"
                    type="text"
                    size="md"
                    prependText="$"
                    error={errors.income_to}
                    mask="currency"
                  />
                )}
              />
            </div>
          </div>
          <RevSelect
            name="education"
            className="w-full sm:max-w-[280px]"
            label="Education"
            isLabelBold
            options={educationOptions}
            control={control}
            error={errors.education}
            size="md"
            placeholder="Select"
          />
          <RevSelect
            name="occupation"
            className="w-full sm:max-w-[280px]"
            label="Occupation"
            isLabelBold
            options={occupationOptions}
            control={control}
            error={errors.occupation}
            size="md"
            placeholder="Select"
          />
          <RevRadioGroup
            name="marital_status"
            label="Marital status"
            options={maritalStatusOptions}
            control={control}
            displayDirection="horizontal"
            error={errors.marital_status}
            isLabelBold
          />
          <RevRadioGroup
            name="family_size"
            label="Family size"
            options={familySizeOptions}
            control={control}
            displayDirection="horizontal"
            error={errors.family_size}
            isLabelBold
          />
        </div>
      </RevCard>

      <RevCard className="py-4 px-6" title="Locations" paddingTitle="py-4 px-6">
        <Controller
          name="locations"
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <RevLocation
              name="locations"
              label=""
              isLabelBold
              value={value}
              onChange={onChange}
              error={error}
              fetchSuggestions={fetchCities}
            />
          )}
        />
      </RevCard>

      <RevCard
        className="py-4 px-6"
        title="Psychographic Targeting"
        paddingTitle="py-4 px-6"
      >
        <div className="space-y-6">
          <Controller
            name="interests"
            control={control}
            defaultValue={[]}
            rules={{
              validate: (value) =>
                (value && value.length > 0) || "Select at least one interest.",
            }}
            render={({ field, fieldState }) => (
              <RevInterestsInput
                name={field.name}
                value={field.value}
                onChange={field.onChange}
                error={fieldState.error}
                label="Interests"
                isLabelBold
                required
              />
            )}
          />
          {coreValuesOptions.length > 0 && (
            <div>
              <p className="typography-body-bold-sm mb-2 text-monochrome-900">
                Core Values
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RevSelect
                  name="main_core_value"
                  className="w-full"
                  label="Primary Value"
                  options={coreValuesOptions}
                  value={mainValue}
                  size="md"
                  placeholder="Select"
                  searchable
                  searchDebounceMs={300}
                  onChange={(value) => {
                    lastChangedRef.current = "main";
                    setValue("main_core_value", String(value), {
                      shouldDirty: true,
                    });
                  }}
                />
                <RevSelect
                  name="secondary_core_value"
                  className="w-full"
                  label="Secondary Value"
                  options={coreValuesOptions}
                  size="md"
                  value={secondaryValue}
                  placeholder="Select"
                  searchable
                  searchDebounceMs={300}
                  onChange={(value) => {
                    lastChangedRef.current = "secondary";
                    setValue("secondary_core_value", String(value), {
                      shouldDirty: true,
                    });
                  }}
                />
              </div>
            </div>
          )}

          <RevRadioGroup
            name="preferred_browsing_method"
            label="Preferred Browsing Method"
            options={browseMethodOptions}
            control={control}
            displayDirection="horizontal"
            error={errors.preferred_browsing_method}
            isLabelBold
          />
          <RevRadioGroup
            name="preferred_buying_method"
            label="Preferred Buying Method"
            options={buyingMethodOptions}
            control={control}
            displayDirection="horizontal"
            error={errors.preferred_buying_method}
            isLabelBold
          />
          <div className="space-y-2">
            <p className="typography-body-bold-sm mb-2 text-monochrome-900">
              Personality Sliders
            </p>
            <Controller
              name="extroverted_introverted"
              control={control}
              defaultValue={4}
              render={({ field }) => (
                <RevSlider
                  leftLabel="Introverted"
                  rightLabel="Extroverted"
                  value={field.value}
                  className="max-w-[430px]"
                  onChange={field.onChange}
                  name={field.name}
                />
              )}
            />
            <Controller
              name="serious_playful"
              control={control}
              defaultValue={4}
              render={({ field }) => (
                <RevSlider
                  leftLabel="Serious"
                  rightLabel="Playful"
                  value={field.value}
                  className="max-w-[430px]"
                  onChange={field.onChange}
                  name={field.name}
                />
              )}
            />
            <Controller
              name="active_sedentary"
              control={control}
              defaultValue={4}
              render={({ field }) => (
                <RevSlider
                  leftLabel="Sedentary"
                  rightLabel="Active"
                  value={field.value}
                  className="max-w-[430px]"
                  onChange={field.onChange}
                  name={field.name}
                />
              )}
            />
            <Controller
              name="follower_trendsetter"
              control={control}
              defaultValue={4}
              render={({ field }) => (
                <RevSlider
                  leftLabel="Follower"
                  rightLabel="Trendsetter"
                  value={field.value}
                  className="max-w-[430px]"
                  onChange={field.onChange}
                  name={field.name}
                />
              )}
            />
            <Controller
              name="spender_saver"
              control={control}
              defaultValue={4}
              render={({ field }) => (
                <RevSlider
                  leftLabel="Saver"
                  rightLabel="Spender"
                  value={field.value}
                  className="max-w-[430px]"
                  onChange={field.onChange}
                  name={field.name}
                />
              )}
            />
          </div>
        </div>
      </RevCard>

      <RevDialog
        isOpen={showUrlDialog}
        setIsOpen={setShowUrlDialog}
        title="Add Picture via URL"
        classNameContainer="z-50"
        btnList={[
          <RevButton
            text="Cancel"
            variant="secondary"
            buttonSize="medium"
            handleClick={() => {
              setShowUrlDialog(false);
            }}
            key="cancel-btn-persona-picture-url"
          />,
          <RevButton
            text="Submit"
            variant="primary"
            buttonSize="medium"
            handleClick={handleAddUrlImage}
            disabled={!imageUrlPreview}
            key="submit-btn-persona-picture-url"
          />,
        ]}
      >
        <div className="space-y-4 w-full">
          <p className="typography-body-sm text-monochrome-900">
            Enter the URL for the image below.
          </p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-monochrome-50 border border-monochrome-908 rounded flex items-center justify-center flex-shrink-0">
              {imageUrlPreview ? (
                <img
                  src={imageUrlPreview}
                  alt="URL Preview"
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faUser}
                  className="text-monochrome-400 text-lg"
                />
              )}
            </div>
            <RevInput
              name="imageUrlInput"
              type="url"
              size="md"
              placeholder="Add an image URL..."
              value={imageUrlInput}
              onChange={handleUrlInputChange}
              className="flex-grow"
              label=""
            />
          </div>
        </div>
      </RevDialog>
    </div>
  );
};
